// Pictures for question options (a choice between visual variants).
// Leaf on purpose, like question-contract.ts: no React.
//
// An option names the screenshotId an app_screenshot call returned in this
// thread (src/apps/tracer/tools.mjs). The picture is that call's retained
// output, so nothing new is stored or fetched; an id with no screenshot behind
// it simply leaves the option as text.
import type { AskedQuestion } from './question-contract';

/** Mirrors the tool name in src/apps/tracer/tools.mjs. */
export const APP_SCREENSHOT = 'app_screenshot';

/** screenshotId → an image URL the browser can show. */
export type OptionImages = Readonly<Record<string, string>>;

type ThreadPart = { type: string; toolName?: string; result?: unknown };

export function previewImageIds(questions: readonly AskedQuestion[]): string[] {
  const ids = questions.flatMap((question) =>
    question.options.flatMap((option) => (option.previewImage ? [option.previewImage] : [])),
  );
  return [...new Set(ids)];
}

/** Thread messages as the assistant-ui state holds them. */
export function screenshotImages(
  messages: readonly unknown[],
  ids: readonly string[],
): OptionImages {
  if (!ids.length) return {};
  const wanted = new Set(ids);
  const found: Record<string, string> = {};
  for (const message of messages as readonly { content?: readonly ThreadPart[] }[])
    for (const part of message?.content ?? []) {
      if (part.type !== 'tool-call' || part.toolName !== APP_SCREENSHOT) continue;
      const shot = part.result as
        | { screenshotId?: unknown; mediaType?: unknown; data?: unknown }
        | undefined;
      if (
        typeof shot?.screenshotId === 'string' &&
        wanted.has(shot.screenshotId) &&
        shot.mediaType === 'image/png' &&
        typeof shot.data === 'string'
      )
        found[shot.screenshotId] = `data:image/png;base64,${shot.data}`;
    }
  return found;
}

/** The option's picture, when its screenshot is in this thread. */
export const optionImage = (images: OptionImages, previewImage: string | undefined) =>
  previewImage && Object.hasOwn(images, previewImage) ? images[previewImage] : undefined;

export const hasOptionImages = (question: AskedQuestion | undefined, images: OptionImages) =>
  Boolean(question?.options.some((option) => optionImage(images, option.previewImage)));
